import { useEffect, useRef, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTenant } from '@/hooks/useTenant';
import { useCart } from '@/hooks/useCart';
import { useThemePages } from '@/hooks/useThemePages';
import { getStoreCapabilities } from '@/lib/accountType';
import TemplateRenderer from '@/components/feature/TemplateRenderer';
import TenantNavbar from './TenantNavbar';
import TenantFooter from './TenantFooter';
import TenantSidebar from './TenantSidebar';
import ChatLiveWidget from '@/components/feature/ChatLiveWidget';
import { isStyledTheme } from '@/lib/themeUsability';

// ── Correspondance chemins du thème → routes de l'application ────────
// Les gabarits de thème utilisent des liens « classiques » (/produits,
// /apropos…). On les traduit vers les vraies routes pour rester en SPA.
const THEME_PATH_MAP: Record<string, string> = {
  '/': '/',
  '/produits': '/products',
  '/produit': '/products',
  '/products': '/products',
  '/boutique': '/products',
  '/service': '/services',
  '/services': '/services',
  '/apropos': '/page/apropos',
  '/about': '/page/apropos',
  '/contact': '/page/contact',
  '/blog': '/page/blog',
  '/faq': '/page/faq',
  '/gallery': '/page/gallery',
  '/galerie': '/page/gallery',
  '/project': '/page/project',
  '/projets': '/page/project',
  '/portfolio': '/portfolio',
  '/equipe': '/page/equipe',
  '/team': '/page/equipe',
  '/partenaires': '/page/partenaires',
  '/partners': '/page/partenaires',
  '/booking': '/booking',
  '/reservation': '/booking',
  '/panier': '/cart',
  '/cart': '/cart',
  '/commande': '/checkout',
  '/checkout': '/checkout',
  '/categories': '/categories',
  '/hebergements': '/hebergements',
  '/hebergement': '/hebergements',
  '/evenement': '/evenements',
  '/evenements': '/evenements',
  '/events': '/evenements',
  '/formations': '/formations',
  '/formation': '/formations',
  '/cours': '/formations',
  '/vendeurs': '/vendeurs',
  '/marketplace': '/page/marketplace',
  '/conditionsdutilisation': '/page/conditionsdutilisation',
  '/politique': '/page/politique',
  '/retours': '/page/retours',
};

function resolveThemedHref(href: string): { path: string } | { external: string } | null {
  if (!href) return null;
  if (href.startsWith('#') || href.startsWith('http://') || href.startsWith('https://') || href.startsWith('mailto:') || href.startsWith('tel:')) {
    return null;
  }

  const path = href.split(/[?#]/)[0];
  const suffix = href.slice(path.length);

  // Pages d'authentification → elles RESTENT sur la boutique / marketplace.
  // Le client n'est plus renvoyé vers le site principal zifek.fr : la
  // connexion et l'inscription client s'ouvrent sur le sous-domaine courant.
  if (['/connexion', '/login', '/login-client'].includes(path)) return { path: '/login-client' };
  if (['/register-client', '/creationcompte', '/register'].includes(path)) return { path: '/register-client' };
  if (['/inscription', '/vendeur/inscription'].includes(path)) return { path: '/vendeur/inscription' };

  const mapped = THEME_PATH_MAP[path] ?? path;
  return { path: `${mapped}${suffix}` };
}

interface TenantThemeShellProps {
  children: ReactNode;
}

export default function TenantThemeShell({ children }: TenantThemeShellProps) {
  const navigate = useNavigate();
  const { theme, tenant } = useTenant();
  const { count: cartCount } = useCart();
  const { pages } = useThemePages(['header', 'navmenu', 'footer']);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const caps = getStoreCapabilities(tenant?.type);
  // Un thème « échantillon » (feuille de style quasi vide) ne doit pas être
  // rendu tel quel : on retombe sur la vitrine dynamique intégrée (navbar,
  // bannière, produits, charte) qui reflète réellement la boutique.
  const themeUsable = isStyledTheme(theme?.stylesheet);
  const hasThemeStylesheet = themeUsable;
  const hasHeader = themeUsable && Boolean(pages.header?.content?.trim());
  const hasNavmenu = hasHeader && Boolean(pages.navmenu?.content?.trim());
  const hasFooter = themeUsable && Boolean(pages.footer?.content?.trim());

  // Interception des liens du thème : navigation SPA + ouverture du menu mobile
  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;

      // Bouton hamburger → ouvre/ferme le menu (gabarit de thème statique)
      const hamburger = target.closest('.hamburger');
      if (hamburger) {
        e.preventDefault();
        el.querySelectorAll('.navmenu').forEach((nav) => nav.classList.toggle('open'));
        return;
      }

      // Sous-menu dépliable mobile
      const dropdown = target.closest('.has-dropdown > a');
      if (dropdown) {
        const parent = dropdown.parentElement;
        if (parent && parent.classList.contains('has-dropdown')) {
          e.preventDefault();
          parent.classList.toggle('open');
          return;
        }
      }

      const anchor = target.closest('a');
      if (!anchor) return;
      if (anchor.getAttribute('target') === '_blank') return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      const resolved = resolveThemedHref(href);
      if (!resolved) return;

      e.preventDefault();
      el.querySelectorAll('.navmenu.open').forEach((nav) => nav.classList.remove('open'));

      if ('external' in resolved) {
        window.location.href = resolved.external;
      } else {
        navigate(resolved.path);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    };

    el.addEventListener('click', onClick);
    return () => el.removeEventListener('click', onClick);
  }, [navigate]);

  return (
    <div ref={wrapperRef} className={`flex-1 flex flex-col ${hasThemeStylesheet ? '' : 'zifek-theme-root'}`}>
      {hasHeader ? <TemplateRenderer content={pages.header.content} /> : <TenantNavbar />}
      {hasNavmenu && <TemplateRenderer content={pages.navmenu.content} />}
      <main className="flex-1">{children}</main>

      {/* Panier flottant : garde l'accès au panier quand l'en-tête vient du thème */}
      {hasHeader && caps.products && cartCount > 0 && (
        <Link
          to="/cart"
          className="fixed bottom-24 right-5 z-[70] flex items-center gap-2 px-4 h-12 rounded-full bg-primary-500 text-background-50 shadow-lg hover:bg-primary-600 transition-colors no-underline whitespace-nowrap"
          aria-label="Voir mon panier"
        >
          <i className="ri-shopping-cart-line text-lg"></i>
          <span className="text-sm font-semibold">{cartCount}</span>
        </Link>
      )}

      {hasFooter ? <TemplateRenderer content={pages.footer.content} /> : <TenantFooter />}

      {/* Barre latérale : accès rapide aux sections (dont Hébergements) */}
      <TenantSidebar />

      {/* Chat live du marchand (affiché seulement s'il l'a activé) */}
      <ChatLiveWidget />
    </div>
  );
}